import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { COLLECTION_META, PRODUCTS } from "../data/catalog";
import { useEtsyListings, withEtsy } from "../data/etsy";
import { ETSY_SHOP } from "../data/site";
import { ProductCard } from "./ProductCard";
import { Reveal } from "./motion";

export default function CollectionPage() {
  const { slug } = useParams();
  const etsyListings = useEtsyListings();
  const meta = COLLECTION_META.find((c) => c.slug === slug);
  if (!meta) return <Navigate to="/" replace />;

  const items = PRODUCTS.filter((p) => p.collection === meta.name).map((p) => withEtsy(p, etsyListings));

  return (
    <main data-testid={`collection-page-${slug}`}>
      <div className={`${meta.bg} border-b border-navy/10`}>
        <div className="mx-auto max-w-[1600px] px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-24">
          <Reveal>
            <Link
              to="/"
              data-testid="collection-back-link"
              className="group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-navy/60 transition-colors duration-300 hover:text-navy"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              All collections
            </Link>
            <h1 className="mt-10 max-w-5xl font-playfair text-5xl leading-[0.92] tracking-tight text-navy md:text-7xl lg:text-8xl">
              {meta.name}
            </h1>
            <p className="mt-8 max-w-xl text-sm leading-relaxed text-navy/70 md:text-base">{meta.types}</p>
            <p className="mt-4 max-w-xl font-playfair text-lg italic leading-relaxed text-navy/60 md:text-xl">
              “{meta.vibe}”
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <span data-testid="collection-product-count" className="text-[11px] font-medium uppercase tracking-[0.3em] text-navy/50">
                {items.length} pieces — every order fulfilled via Etsy
              </span>
              <a
                href={ETSY_SHOP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="collection-shop-etsy-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-navy px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
              >
                Shop on Etsy
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => (
            <Reveal key={`${item.name}-${i}`} delay={Math.min((i % 4) * 0.06, 0.24)} className="h-full">
              <ProductCard item={item} setId={slug} />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-20 border-t border-navy/15 pt-10">
          <p className="max-w-xl text-sm leading-relaxed text-navy/60">
            These pieces are being prepared in the studio and listed on Etsy as they're finished. Anything marked
            <span className="font-medium text-navy"> Coming Soon</span> links to the shop so you can follow along and
            catch it the day it drops.
          </p>
        </Reveal>
      </div>
    </main>
  );
}
